/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import SummaryIndicator from './SummaryIndicator'
import SieauEChart from 'components/echart/SieauEChart'
import { iaeauResultsShape } from 'territory/shapes/A3PShapes'
import { NoDataPanel } from 'components/card/NoDataCard'
import { yAutomaticScaleValues } from 'components/echart/EChartUtils'
import { last, range, fromPairs, keyBy, sortBy, uniq, keys } from 'lodash'
import { blackColor, disabledColor, grey } from 'utils/constants/ColorTheme'
import { Grid } from '@mui/material'
import { round } from 'mathjs'
import { formatMilliers } from 'utils/StringUtil'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { VP_LEVELS, VP_LEVELS_PARAMETERS } from 'territory/constants/A3PConstants'
import { hasValue } from 'utils/NumberUtil'
import moment from 'moment'

const chartHeight = 500

const FollowAndPredictionConsoChart = ({ iaeauResults, vpLevel, vpLevelValues = [] }) => {
    const VPRestrictedLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.RESTRICTED].label, v => hasValue(v) ? String(v) : null)
    const VPNormalLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.NORMAL].label, v => hasValue(v) ? String(v) : null)
    const VPHighLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.HIGH].label, v => hasValue(v) ? String(v) : null)
    const VPVeryHighLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.VERY_HIGH].label, v => hasValue(v) ? String(v) : null)

    const exportHeaders = [i18n.date, i18n.observedConsumption, i18n.P20, i18n.P80, i18n.P90]

    const getContent = () => {
        const observed = iaeauResults?.prelevements_observes ?? []
        const projections = iaeauResults?.prelevements_projections ?? []
        if (!observed.length && !projections.length) return <NoDataPanel />
        const grid = [{
            id: 'followAndPredictionConsoChart',
            right: 100,
            width: '85%',
            top: 50,
            bottom: 40,
        }]

        const daysInYear = moment().isLeapYear() ? 366 : 365

        const xAxis = [{
            gridIndex: 0,
            type: 'category',
            position: 'bottom',
            nameGap: 35,
            nameLocation: 'center',
            boundaryGap: false,
            data: range(1, daysInYear + 1),
            axisLine: {
                show: true,
                lineStyle: { color: '#000' },
            },
            axisLabel: {
                interval: (_, value) => {
                    const v = parseInt(value)
                    if (v === 1 || v === daysInYear) return true
                    return moment().dayOfYear(v).date() === 1
                },
                formatter: (value) => {
                    const v = parseInt(value)
                    const date = moment().dayOfYear(v)
                    if (v === 1 || v === daysInYear) return date.format('DD/MM/YYYY')
                    return date.format('MM/YY')
                },
            },
            axisTick: {
                interval: (_, value) => {
                    const v = parseInt(value)
                    if (v === 1 || v === daysInYear) return true
                    return moment().dayOfYear(v).date() === 1
                },
            },
            splitLine: {
                show: true,
                interval: (_, value) => {
                    const v = parseInt(value)
                    if (v === 1 || v === daysInYear) return false
                    return moment().dayOfYear(v).date() === 1
                },
            },
        }]

        const volumeValues = [
            ...observed.map(({ irrig_observe }) => irrig_observe),
            ...projections.flatMap(({ p20, p80, p90 }) => [p20, p80, p90]),
        ].filter(hasValue)

        const { min, max, interval, splitNumber } = yAutomaticScaleValues(volumeValues)

        const yAxis = [{
            gridIndex: 0,
            type: 'value',
            name: `${i18n.accumulatedIrrigation} (${i18n.metersSymbol}3)`,
            nameLocation: 'center',
            nameGap: 50,
            min,
            max,
            interval,
            splitNumber,
            axisLine: {
                show: true,
                lineStyle: { color: '#000' },
            },
            axisTick: {
                show: true,
            },
        }]

        const lastObservedValue = last(observed)
        const junctionPoint = hasValue(lastObservedValue?.irrig_observe) ? [[String(lastObservedValue.jour), lastObservedValue.irrig_observe]] : []

        const observedDatas = observed.map(({ jour, irrig_observe }) => [String(jour), irrig_observe])

        const p80Datas = [
            ...junctionPoint,
            ...projections.map(({ jour, p80 }) => [String(jour), p80]),
        ]
        const p20Datas = [
            ...junctionPoint,
            ...projections.map(({ jour, p20 }) => [String(jour), p20]),
        ]
        const p90Datas = [
            ...junctionPoint,
            ...projections.map(({ jour, p90 }) => [String(jour), p90]),
        ]

        const observedByDay = fromPairs(observed.map(({ jour, irrig_observe }) => [jour, irrig_observe]))
        const projectionsByDay = keyBy(projections, 'jour')
        const exportRows = sortBy(uniq([...keys(observedByDay), ...keys(projectionsByDay)].map(Number))).map(day => ({
            [i18n.date]: moment().dayOfYear(day).format('DD/MM/YYYY'),
            [i18n.observedConsumption]: observedByDay[day],
            [i18n.P20]: projectionsByDay[day]?.p20,
            [i18n.P80]: projectionsByDay[day]?.p80,
            [i18n.P90]: projectionsByDay[day]?.p90,
        }))

        const baseSeries = {
            xAxisIndex: 0,
            yAxisIndex: 0,
            type: 'line',
            symbol: 'none',
            data: [],
        }

        const vpThresholds = [
            { name: VPRestrictedLevelLabel ?? i18n.VPRestricted, value: vpLevelValues[VP_LEVELS.RESTRICTED], key: VP_LEVELS.RESTRICTED },
            { name: VPNormalLevelLabel ?? i18n.VPNormal, value: vpLevelValues[VP_LEVELS.NORMAL], key: VP_LEVELS.NORMAL },
            { name: VPHighLevelLabel ?? i18n.VPHigh, value: vpLevelValues[VP_LEVELS.HIGH], key: VP_LEVELS.HIGH },
            { name: VPVeryHighLevelLabel ?? i18n.VPVeryHigh, value: vpLevelValues[VP_LEVELS.VERY_HIGH], key: VP_LEVELS.VERY_HIGH },
        ].filter(({ value }) => hasValue(value))

        const series = [{
            ...baseSeries,
            markLine: {
                silent: true,
                symbol: ['none', 'none'],
                lineStyle: {
                    color: disabledColor,
                    type: 'solid',
                },
                data: vpThresholds.map(({ name, value, key }) => {
                    const isSelected = key === vpLevel
                    return {
                        name,
                        yAxis: value,
                        label: {
                            position: 'insideStart',
                            formatter: () => `${name} : ${formatMilliers(value)} m3`,
                            color: blackColor,
                            textBorderColor: '#fff',
                            textBorderWidth: 3,
                            fontWeight: isSelected ? 'bold' : 'normal',
                        },
                        ...(isSelected ? { lineStyle: { width: 3 } } : {}),
                    }
                }),
            },
        }, {
            ...baseSeries,
            markLine: {
                symbol: ['none', 'none'],
                label: {
                    position: 'middle',
                    formatter: ({ data }) => `${data.name} : J${data.xAxis}`,
                },
                lineStyle: {
                    color: disabledColor,
                    type: 'solid',
                },
                data: hasValue(lastObservedValue?.jour) ? [{
                    name: i18n.latestObservation,
                    xAxis: String(lastObservedValue.jour),
                }] : [],
            },
        }, {
            ...baseSeries,
            name: i18n.observedConsumption,
            isData: true,
            data: observedDatas,
            itemStyle: {
                color: blackColor,
            },
        }, {
            ...baseSeries,
            name: i18n.P20,
            data: p20Datas,
            itemStyle: {
                color: '#00AA00',
            },
            lineStyle: {
                type: 'dotted',
            },
        }, {
            ...baseSeries,
            name: i18n.P80,
            data: p80Datas,
            itemStyle: {
                color: '#0000ff',
            },
            lineStyle: {
                type: 'dashed',
            },
        }, {
            ...baseSeries,
            name: i18n.enveloppeP20P90,
            data: p20Datas,
            stack: 'p20p90Envelope',
            lineStyle: { opacity: 0 },
            silent: true,
            tooltip: { show: false },
            z: -2,
        }, {
            ...baseSeries,
            name: i18n.enveloppeP20P90,
            data: p90Datas.map(([jour, p90], idx) => {
                const p20 = p20Datas[idx][1]
                return [jour, hasValue(p90) && hasValue(p20) ? p90 - p20 : null]
            }),
            stack: 'p20p90Envelope',
            lineStyle: { opacity: 0 },
            areaStyle: { color: grey, opacity: 0.5 },
            tooltip: { show: false },
            z: -1,
        }, {
            ...baseSeries,
            name: i18n.P90,
            data: p90Datas,
            itemStyle: {
                color: '#ff0000',
            },
            lineStyle: {
                type: 'dotted',
            },
        }]

        return (
            <Grid size={12}>
                <SieauEChart
                    option={{
                        animation: false,
                        grid,
                        xAxis,
                        yAxis,
                        series,
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                axis: 'x',
                                label: {
                                    precision: 0,
                                    formatter: data => `${moment().dayOfYear(data.value).format('DD/MM/YYYY')}`,
                                },
                            },
                            valueFormatter: value => (hasValue(value) ? formatMilliers(round(value, 2), true) : '-'),
                        },
                    }}
                    toolsOptions={{
                        withLegend: true,
                        withToolLog: true,
                        withToolMarker: true,
                        withToolLine: true,
                        withToolLegend: true,
                    }}
                    withYZoomModal
                    withToolFullScreen
                    withDataZoom
                    style={{ height: chartHeight }}
                    exportDatas={{
                        fileName: i18n.sampleTrackingAndForecasting,
                        headers: exportHeaders,
                        datas: exportRows,
                    }}
                />
            </Grid>
        )
    }

    return (
        <SummaryIndicator
            title={i18n.sampleTrackingAndForecasting}
            bgColor='white'
            content={getContent()}
        />
    )
}

FollowAndPredictionConsoChart.propTypes = {
    iaeauResults: iaeauResultsShape,
    vpLevel: PropTypes.oneOf(Object.values(VP_LEVELS)),
    vpLevelValues: PropTypes.arrayOf(PropTypes.number),
}

export default FollowAndPredictionConsoChart